import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Plus, Edit2, Trash2, Mail, MapPin, Shield, Eye, EyeOff, UserCheck, Copy, Check, KeyRound, RotateCcw, FileDown, Cpu, Key, Calendar, AlertCircle, ClipboardList, ChevronDown, Link as LinkIcon, Clock, ArrowUpRight, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { PersonalboqenWizard } from '../../components/PersonalboqenWizard';
import { PersonalboqenEditModal } from '../../components/PersonalboqenEditModal';

const API = process.env.REACT_APP_BACKEND_URL;

export const EmployeeManagementEnhanced = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Hardware Export Dialog
  const [showHardwareDialog, setShowHardwareDialog] = useState(false);
  const [hardwareFilter, setHardwareFilter] = useState('all');
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // 🆕 Employee Limits State
  const [employeeLimits, setEmployeeLimits] = useState({
    total_employees: 0,
    location_count: 0,
    free_limit: 0,
    extra_employees: 0,
    cost_per_month: 0,
    price_per_employee: 0.99
  });
  const [limitsLoading, setLimitsLoading] = useState(true);
  
  // 🆕 Password Dialog State (für Einmal-Passwort nach Mitarbeiter-Erstellung)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [createdEmployeeInfo, setCreatedEmployeeInfo] = useState({ name: '', password: '', email: '' });
  const [passwordCopied, setPasswordCopied] = useState(false);

  // 🆕 Personalbogen state
  const [showPersonalboqenWizard, setShowPersonalboqenWizard] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [personalboqens, setPersonalboqens] = useState([]);
  const [editingBogen, setEditingBogen] = useState(null);

  // 🆕 Employee-Limit-Dialog (billing_v2 Add-on Upgrade)
  const [showEmployeeLimitDialog, setShowEmployeeLimitDialog] = useState(false);
  const [employeeLimitDetail, setEmployeeLimitDetail] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    location_ids: [],
    role: 'employee',
    chip_number: '',
    key_number: '',
    received_hardware_at: '',
    returned_hardware_at: '',
    strasse: '',
    plz_ort: '',
  });

  useEffect(() => {
    fetchData();
    fetchEmployeeLimits();
    fetchPersonalboqens();
  }, []);

  const fetchPersonalboqens = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/personalbogen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPersonalboqens(res.data || []);
    } catch { /* silent */ }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch employees and locations in parallel
      const [empResponse, locResponse] = await Promise.all([
        axios.get(`${API}/api/admin/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/user/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEmployees(empResponse.data);
      setLocations(locResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeLimits = async () => {
    setLimitsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/employee-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeLimits(response.data);
    } catch (error) {
      console.error('Error fetching employee limits:', error);
      // Don't show error toast for limits - it's not critical
    } finally {
      setLimitsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const newLocationIds = prev.location_ids.includes(locationId)
        ? prev.location_ids.filter(id => id !== locationId)
        : [...prev.location_ids, locationId];
      return { ...prev, location_ids: newLocationIds };
    });
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      location_ids: [],
      role: 'employee',
      chip_number: '',
      key_number: '',
      received_hardware_at: '',
      returned_hardware_at: '',
      strasse: '',
      plz_ort: '',
    });
  };

  const handleCreateEmployee = async (closeDialog = false) => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    if (formData.location_ids.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Standort aus');
      return;
    }

    const createdEmail = formData.email;
    const createdName = `${formData.first_name} ${formData.last_name}`.trim();

    try {
      const token = localStorage.getItem('token');
      // 🆕 Kein Passwort senden – Backend generiert automatisch 4-stelligen PIN
      const response = await axios.post(`${API}/api/admin/employees/create-enhanced`, {
        email: formData.email,
        name: createdName,
        location_ids: formData.location_ids,
        role: formData.role,
        strasse: formData.strasse || null,
        plz_ort: formData.plz_ort || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Form zurücksetzen
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        location_ids: formData.location_ids,
        role: formData.role,
        strasse: '',
        plz_ort: '',
      });

      if (closeDialog) {
        setShowCreateDialog(false);
        resetForm();
      }

      fetchData();
      fetchEmployeeLimits();

      // 🆕 Generierten PIN vom Backend anzeigen
      const generatedPin = response.data.generated_pin || '????';
      setCreatedEmployeeInfo({ name: createdName, password: generatedPin, email: createdEmail });
      setPasswordCopied(false);
      setShowPasswordDialog(true);

    } catch (error) {
      console.error('Error creating employee:', error);
      const detail = error.response?.data?.detail;
      // Fix 1: employee_limit_reached → eigenen Dialog zeigen statt generischen Toast
      if (detail && typeof detail === 'object' && detail.error === 'employee_limit_reached') {
        setEmployeeLimitDetail(detail);
        setShowEmployeeLimitDialog(true);
        setShowCreateDialog(false);
      } else if (detail && typeof detail === 'object' && detail.errors) {
        detail.errors.forEach(err => toast.error(err));
      } else if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Fehler beim Erstellen des Mitarbeiters');
      }
    }
  };

  const openEditDialog = (employee) => {
    const nameParts = employee.name?.split(' ') || ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setSelectedEmployee(employee);
    setFormData({
      email: employee.email,
      first_name: firstName,
      last_name: lastName,
      initial_password: '',
      location_ids: employee.location_ids || [],
      role: employee.role,
      chip_number: employee.chip_number || '',
      key_number: employee.key_number || '',
      received_hardware_at: employee.received_hardware_at || '',
      returned_hardware_at: employee.returned_hardware_at || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: `${formData.first_name} ${formData.last_name}`.trim(),
        email: formData.email,
        location_ids: formData.location_ids,
        role: formData.role,
        chip_number: formData.chip_number || null,
        key_number: formData.key_number || null,
        received_hardware_at: formData.received_hardware_at || null,
        returned_hardware_at: formData.returned_hardware_at || null
      };

      const response = await axios.put(`${API}/api/admin/employees/${selectedEmployee.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.email_changed && response.data.pin) {
        toast.success(`E-Mail geändert. Neuer PIN (${response.data.pin}) wurde an neue E-Mail gesendet.`, { duration: 6000 });
        setCreatedEmployeeInfo({ name: updateData.name, password: response.data.pin, email: formData.email });
        setPasswordCopied(false);
        setShowPasswordDialog(true);
      } else {
        toast.success('Mitarbeiter aktualisiert');
      }
      
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedEmployee) return;
    if (!window.confirm(`Passwort für ${selectedEmployee.name} zurücksetzen? Ein neuer PIN wird per E-Mail gesendet.`)) return;
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/admin/employees/${selectedEmployee.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const pin = response.data.pin;
      setCreatedEmployeeInfo({ name: selectedEmployee.name, password: pin, email: selectedEmployee.email });
      setPasswordCopied(false);
      setShowEditDialog(false);
      setShowPasswordDialog(true);
      toast.success(`PIN wurde per E-Mail an ${selectedEmployee.email} gesendet`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleHardwareExport = async () => {
    setExportingPdf(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/admin/employees/hardware-pdf?filter_type=${hardwareFilter}`;
      if (hardwareFilter === 'selection' && selectedForExport.length > 0) {
        url += `&ids=${selectedForExport.join(',')}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `hardware_liste_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      toast.success('Hardware-Liste exportiert');
      setShowHardwareDialog(false);
    } catch (error) {
      toast.error('Fehler beim PDF-Export');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Möchten Sie diesen Mitarbeiter wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Mitarbeiter gelöscht');
      fetchData();
      fetchEmployeeLimits(); // 🆕 Refresh limits after deleting employee
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleImpersonate = async (employee) => {
    setImpersonating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get impersonation token from backend
      const response = await axios.post(
        `${API}/api/admin/impersonate/${employee.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { access_token, impersonated_user } = response.data;
      
      // Store original admin credentials
      const impersonationData = {
        original_token: token,
        original_user: JSON.parse(localStorage.getItem('user')),
        impersonated_user: impersonated_user
      };
      localStorage.setItem('impersonation', JSON.stringify(impersonationData));

      // OLD format für Kompatibilität
      localStorage.setItem('impersonated_user', JSON.stringify({
        id: impersonated_user.id,
        name: impersonated_user.name,
        email: impersonated_user.email,
        role: impersonated_user.role,
        employee_role: impersonated_user.employee_role,
        impersonated_by_role: 'admin'
      }));

      // Neues Token + User setzen (wie Superadmin-Impersonation)
      // → useAuth() und ProtectedRoute lesen die korrekte Rolle nach Reload
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(impersonated_user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      toast.success(`Sie sind jetzt als ${impersonated_user.name} angemeldet`, {
        description: 'Klicken Sie auf "Zurück zum Admin" um zurückzukehren'
      });

      // Vollständiger Reload → AuthContext neu initialisieren mit korrekter Rolle
      const targetUrl = impersonated_user.role === 'stationsleitung'
        ? '/employee/dashboard'
        : '/employee/dashboard';
      window.location.href = targetUrl;
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Wechseln zum Mitarbeiter-Dashboard');
    } finally {
      setImpersonating(false);
    }
  };


  const getLocationNames = (locationIds) => {
    if (!locationIds || locationIds.length === 0) return 'Keine Standorte';
    return locationIds
      .map(id => {
        const loc = locations.find(l => l.id === id);
        return loc ? loc.name : id;
      })
      .join(', ');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600 dark:text-gray-400">Lade...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Mitarbeiter</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Verwalten Sie Ihre Mitarbeiter und deren Zugänge
            </p>
          </div>
          <div className="flex gap-2 flex-col sm:flex-row w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => { setHardwareFilter('all'); setSelectedForExport([]); setShowHardwareDialog(true); }}
              className="w-full sm:w-auto flex items-center gap-2"
              data-testid="hardware-export-btn"
            >
              <FileDown className="h-4 w-4" />
              Hardware exportieren
            </Button>
            {/* "+ Neuer Mitarbeiter" Dropdown */}
            <div className="relative w-full sm:w-auto">
              <Button
                onClick={() => setShowNewMenu(v => !v)}
                className="w-full sm:w-auto flex items-center gap-2"
                data-testid="new-employee-btn"
              >
                <Plus className="h-4 w-4" />
                Neuer Mitarbeiter
                <ChevronDown className="h-3.5 w-3.5 ml-0.5" />
              </Button>
              {showNewMenu && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowNewMenu(false)} />
                  <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-xl shadow-xl z-20 overflow-hidden">
                    <button
                      className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                      onClick={() => { setShowNewMenu(false); resetForm(); setShowCreateDialog(true); }}
                      data-testid="simple-create-btn"
                    >
                      <UserCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold">Einfach anlegen</p>
                        <p className="text-xs text-muted-foreground">Name, E-Mail, Rolle – sofort aktiv</p>
                      </div>
                    </button>
                    <div className="border-t border-border" />
                    <button
                      className="w-full flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors text-left"
                      onClick={() => { setShowNewMenu(false); setShowPersonalboqenWizard(true); }}
                      data-testid="onboarding-wizard-btn"
                    >
                      <ClipboardList className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold">Digitaler Personalbogen</p>
                        <p className="text-xs text-muted-foreground">Personalbogen per Link ausfüllen lassen</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 🆕 EMPLOYEE LIMIT BANNER – aktualisiert für billing_v2 */}
        {!limitsLoading && employeeLimits.location_count > 0 && (
          <div className={`rounded-xl p-4 border-2 ${
            employeeLimits.extra_employees > 0
              ? 'bg-orange-50 border-orange-300 dark:bg-orange-950/20 dark:border-orange-800'
              : 'bg-green-50 border-green-300 dark:bg-green-950/20 dark:border-green-800'
          }`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Users className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  employeeLimits.extra_employees > 0 ? 'text-orange-600' : 'text-green-600'
                }`} />
                <div>
                  <p className={`font-semibold ${
                    employeeLimits.extra_employees > 0 ? 'text-orange-900 dark:text-orange-100' : 'text-green-900 dark:text-green-100'
                  }`}>
                    {employeeLimits.total_employees} von {employeeLimits.free_limit + (employeeLimits.extra_employees || 0)} Mitarbeiterplätzen belegt
                  </p>
                  <p className={`text-xs mt-0.5 ${
                    employeeLimits.extra_employees > 0 ? 'text-orange-700 dark:text-orange-300' : 'text-green-700 dark:text-green-300'
                  }`}>
                    {employeeLimits.location_count} Standort{employeeLimits.location_count !== 1 ? 'e' : ''} × 15 Mitarbeiterplätze inkl.
                    {employeeLimits.extra_employees > 0 && ` · +${employeeLimits.extra_employees} via Add-on (+${employeeLimits.cost_per_month.toFixed(2)} €/Monat)`}
                  </p>
                </div>
              </div>
              {/* Upgrade-Button wenn nah am Limit */}
              {employeeLimits.total_employees >= employeeLimits.free_limit && (
                <Button size="sm" variant="outline"
                  onClick={() => navigate('/admin/subscription')}
                  className="shrink-0 text-xs border-orange-400 text-orange-700 hover:bg-orange-50"
                  data-testid="employee-upgrade-btn">
                  <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
                  Add-on buchen
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Employee Count */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-900">
                {employees.length} Mitarbeiter gesamt
              </p>
              <p className="text-sm text-blue-700">
                {employees.filter(e => e.role === 'stationsleitung').length} Stationsleitungen
              </p>
            </div>
          </div>
        </div>

        {/* Employees List */}
        {employees.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-center">Noch keine Mitarbeiter vorhanden</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
                Ersten Mitarbeiter anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* ── Schwebende Personalbögen ─────────────────────────────── */}
            {personalboqens.filter(b => b.status !== 'approved').length > 0 && (
              <div className="space-y-3" data-section="onboarding">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-violet-600" />
                  <h3 className="text-sm font-semibold text-foreground">Digitaler Personalbogen</h3>
                  <span className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 rounded-full">
                    {personalboqens.filter(b => b.status !== 'approved').length} offen
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {personalboqens.filter(b => b.status !== 'approved').map(bogen => {
                    const pe = bogen.personal || {};
                    const ag = bogen.arbeitgeber || {};
                    const displayName = (pe.vorname || pe.familienname)
                      ? `${pe.vorname || ''} ${pe.familienname || ''}`.trim()
                      : ag.berufsbezeichnung || 'Neuer Mitarbeiter';
                    const publicLink = `${window.location.origin}/personalbogen/${bogen.token}`;
                    const statusCfg = {
                      draft: { label: 'Link ausstehend', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
                      admin_filled: { label: 'Wartet auf Mitarbeiter', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
                      link_sent: { label: 'Wartet auf Mitarbeiter', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
                      submitted: { label: 'Zur Prüfung', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
                    };
                    const sCfg = statusCfg[bogen.status] || statusCfg.draft;
                    return (
                      <Card key={bogen.id} className="border-l-4 border-l-violet-400">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-semibold text-sm text-foreground">{displayName}</p>
                              <p className="text-xs text-muted-foreground">{ag.berufsbezeichnung || '–'} · {ag.eintrittsdatum || '–'}</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${sCfg.cls}`}>
                              {sCfg.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              className="flex items-center gap-1 text-xs bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-1 rounded-md font-medium"
                              onClick={() => setEditingBogen(bogen)}
                              data-testid={`edit-bogen-${bogen.id}`}
                            >
                              <Edit2 className="h-3 w-3" /> Bearbeiten
                            </button>
                            {bogen.status !== 'approved' && (
                              <>
                                <button
                                  onClick={() => { navigator.clipboard.writeText(publicLink); toast.success('Link kopiert!'); }}
                                  className="flex items-center gap-1 text-xs bg-muted hover:bg-muted/80 px-2 py-1 rounded-md"
                                  data-testid={`copy-link-${bogen.id}`}
                                >
                                  <LinkIcon className="h-3 w-3" /> Link kopieren
                                </button>
                                {bogen.pin_code && (
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(bogen.pin_code); toast.success(`PIN ${bogen.pin_code} kopiert!`); }}
                                    className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-md font-mono"
                                    title="PIN für Mitarbeiter kopieren"
                                  >
                                    🔑 {bogen.pin_code}
                                  </button>
                                )}
                              </>
                            )}
                            {bogen.status === 'submitted' && (
                              <>
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const res = await axios.get(`${API}/api/personalbogen/${bogen.id}/pdf`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                        responseType: 'blob',
                                      });
                                      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                      const a = document.createElement('a'); a.href = url; a.download = `Personalbogen_${displayName}.pdf`; a.click();
                                    } catch { toast.error('PDF-Fehler'); }
                                  }}
                                  className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-md"
                                  data-testid={`pdf-download-${bogen.id}`}
                                >
                                  <FileDown className="h-3 w-3" /> PDF
                                </button>
                            {/* Vollständige Personalakte (kombiniert + optional Passwort) */}
                            <button
                              onClick={async () => {
                                const withPw = window.confirm(
                                  `Soll das PDF mit dem PIN-Code (${bogen.pin_code || '–'}) als Passwort verschlüsselt werden?\n\nOK = Ja, mit Passwort\nAbbrechen = Nein, ohne Passwort`
                                );
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await axios.get(
                                    `${API}/api/personalbogen/${bogen.id}/full-combined-pdf?with_password=${withPw}`,
                                    { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                  );
                                  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const a = document.createElement('a'); a.href = url;
                                  a.download = `Personalakte_${displayName}${withPw ? '_geschuetzt' : ''}.pdf`; a.click();
                                  toast.success(withPw ? `PDF erstellt – Passwort: ${bogen.pin_code}` : 'PDF erstellt');
                                } catch { toast.error('Fehler beim Erstellen der Personalakte'); }
                              }}
                              className="flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 px-2 py-1 rounded-md font-medium"
                              data-testid={`full-pdf-${bogen.id}`}
                              title="Personalakte: PB + Vertrag + RV-Antrag (optional mit PIN-Passwort)"
                            >
                              <FileDown className="h-3 w-3" /> Personalakte
                            </button>
                            {(bogen.beschaeftigung?.arbeitszeit === 'minijob' || bogen.arbeitgeber?.arbeitszeit === 'minijob') && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const res = await axios.get(`${API}/api/personalbogen/${bogen.id}/rv-pdf`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                          responseType: 'blob',
                                        });
                                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                        const a = document.createElement('a'); a.href = url; a.download = `RV_Antrag_${displayName}.pdf`; a.click();
                                      } catch { toast.error('RV-PDF Fehler'); }
                                    }}
                                    className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-300 px-2 py-1 rounded-md"
                                    data-testid={`rv-pdf-${bogen.id}`}
                                  >
                                    <Shield className="h-3 w-3" /> RV-Antrag
                                  </button>
                                )}
                                <button
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const res = await axios.post(`${API}/api/personalbogen/${bogen.id}/create-account`, {}, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      toast.success(`Account für ${res.data.email} erstellt! Temp-PW: ${res.data.temp_password}`);
                                      fetchData(); fetchPersonalboqens();
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
                                  }}
                                  className="flex items-center gap-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded-md"
                                  data-testid={`create-account-${bogen.id}`}
                                >
                                  <UserCheck className="h-3 w-3" /> Account erstellen
                                </button>
                              </>
                            )}
                            <button
                              onClick={async () => {
                                if (!window.confirm('Bogen löschen?')) return;
                                const token = localStorage.getItem('token');
                                await axios.delete(`${API}/api/personalbogen/${bogen.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                fetchPersonalboqens();
                              }}
                              className="flex items-center gap-1 text-xs bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 px-2 py-1 rounded-md"
                              data-testid={`delete-bogen-${bogen.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Archivierte Personalbögen (abgeschlossene Onboardings) ─── */}
            {personalboqens.filter(b => b.status === 'approved').length > 0 && (
              <div className="space-y-2">
                <details>
                  <summary className="flex items-center gap-2 cursor-pointer list-none">
                    <div className="flex items-center gap-2">
                      <FileDown className="h-4 w-4 text-green-600" />
                      <h3 className="text-sm font-semibold text-foreground">Abgeschlossene Personalbögen</h3>
                      <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">
                        {personalboqens.filter(b => b.status === 'approved').length}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-1">(klicken zum Öffnen)</span>
                  </summary>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {personalboqens.filter(b => b.status === 'approved').map(bogen => {
                      const pe = bogen.personal || {};
                      const ag = bogen.arbeitgeber || {};
                      const displayName = (pe.vorname || pe.familienname)
                        ? `${pe.vorname || ''} ${pe.familienname || ''}`.trim()
                        : ag.berufsbezeichnung || 'Mitarbeiter';
                      const isMinijob = (bogen.beschaeftigung?.arbeitszeit === 'minijob' || ag.arbeitszeit === 'minijob');
                      return (
                        <div key={bogen.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                            <p className="text-xs text-muted-foreground">{ag.berufsbezeichnung || '–'} · {ag.eintrittsdatum || '–'}</p>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await axios.get(`${API}/api/personalbogen/${bogen.id}/pdf`, {
                                    headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
                                  });
                                  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const a = document.createElement('a'); a.href = url; a.download = `Personalbogen_${displayName}.pdf`; a.click();
                                } catch { toast.error('PDF-Fehler'); }
                              }}
                              className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-md flex items-center gap-1"
                              title="Personalbogen herunterladen"
                            >
                              <FileDown className="h-3 w-3" /> PDF
                            </button>
                            {isMinijob && (
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const res = await axios.get(`${API}/api/personalbogen/${bogen.id}/combined-pdf`, {
                                      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
                                    });
                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    const a = document.createElement('a'); a.href = url; a.download = `Personalunterlagen_${displayName}.pdf`; a.click();
                                  } catch { toast.error('Fehler'); }
                                }}
                                className="text-xs bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 px-2 py-1 rounded-md flex items-center gap-1"
                                title="Alle PDFs herunterladen"
                              >
                                <FileDown className="h-3 w-3" /> Alle
                              </button>
                            )}
                            <button
                              onClick={() => setEditingBogen(bogen)}
                              className="text-xs bg-muted text-muted-foreground hover:bg-muted/80 px-2 py-1 rounded-md flex items-center gap-1"
                              title="Bogen anzeigen/bearbeiten"
                            >
                              <Edit2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              </div>
            )}

            {/* ── Mitarbeiter-Grid ────────────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map((employee) => (
              <Card key={employee.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2 truncate">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{employee.name}</span>
                    </span>
                    {employee.role === 'stationsleitung' && (
                      <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{employee.email}</span>
                  </div>

                  <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span className="break-words">{getLocationNames(employee.location_ids)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${
                      employee.role === 'stationsleitung' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {employee.role === 'stationsleitung' ? 'Stationsleitung' : 'Mitarbeiter'}
                    </span>
                    {employee.must_change_password && (
                      <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">
                        Passwort ändern
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleImpersonate(employee)}
                      disabled={impersonating}
                      className="flex-1"
                      title="Als dieser Mitarbeiter anmelden"
                    >
                      <UserCheck className="h-3 w-3 mr-1" />
                      Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(employee)}
                      className="flex-1"
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(employee.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          </div>
        )}

        {/* Personalbogen Wizard */}
        <PersonalboqenWizard
          open={showPersonalboqenWizard}
          onClose={() => setShowPersonalboqenWizard(false)}
          onCreated={(bogen) => {
            fetchPersonalboqens();
            // Direkt zum Bearbeiten öffnen (vor Ort)
            if (bogen) setEditingBogen(bogen);
          }}
        />

        {/* Personalbogen Edit Modal */}
        <PersonalboqenEditModal
          open={!!editingBogen}
          bogen={editingBogen}
          onClose={() => setEditingBogen(null)}
          onSaved={() => {
            setEditingBogen(null);
            fetchPersonalboqens().then(() => {
              // Scroll zum Digitales Onboarding Block
              setTimeout(() => {
                document.querySelector('[data-section="onboarding"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 200);
            });
          }}
        />

        {/* Create Employee Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl flex flex-col max-h-[90dvh] sm:max-h-[90vh] p-0 gap-0">
            <DialogHeader className="px-4 pt-4 pb-3 sm:px-6 sm:pt-6 border-b flex-shrink-0">
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                Neuen Mitarbeiter anlegen
              </DialogTitle>
            </DialogHeader>

            {/* Scrollbarer Formular-Bereich */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 space-y-4"
                 style={{ WebkitOverflowScrolling: 'touch' }}>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Vorname *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Max"
                  />
                </div>

                <div>
                  <Label htmlFor="last_name">Nachname *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Mustermann"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="max@example.com"
                />
              </div>

              <div>
                <Label htmlFor="role">Rolle *</Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="employee">Mitarbeiter</option>
                  <option value="stationsleitung">Stationsleitung</option>
                </select>
              </div>

              {/* Adresse – optional */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="strasse">Straße + Hausnr. <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                  <Input
                    id="strasse"
                    name="strasse"
                    value={formData.strasse}
                    onChange={handleChange}
                    placeholder="z.B. Musterstr. 5"
                  />
                </div>
                <div>
                  <Label htmlFor="plz_ort">PLZ / Ort <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label>
                  <Input
                    id="plz_ort"
                    name="plz_ort"
                    value={formData.plz_ort}
                    onChange={handleChange}
                    placeholder="z.B. 70734 Fellbach"
                  />
                </div>
              </div>

              <div>
                <Label>Standorte * (mehrere möglich)</Label>
                <div className="border rounded-lg p-3 mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {locations.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">Keine Standorte verfügbar</p>
                  ) : (
                    locations.map(location => (
                      <label
                        key={location.id}
                        className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:bg-gray-800 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.location_ids.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{location.name}</p>
                          {location.address && (
                            <p className="text-xs text-gray-600 dark:text-gray-400">{location.address}</p>
                          )}
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {formData.location_ids.length} Standort(e) ausgewählt
                </p>
              </div>
            </div>

            {/* Footer – immer sichtbar, nicht scrollt weg */}
            <DialogFooter className="px-4 py-3 sm:px-6 sm:py-4 border-t flex-shrink-0 gap-2 flex-col sm:flex-row">
              <Button
                variant="outline"
                onClick={() => { setShowCreateDialog(false); resetForm(); }}
                className="w-full sm:w-auto"
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => handleCreateEmployee(false)}
                className="w-full sm:w-auto"
                variant="secondary"
              >
                <Plus className="mr-2 h-4 w-4" />
                Weitere hinzufügen
              </Button>
              <Button
                onClick={() => handleCreateEmployee(true)}
                className="w-full sm:w-auto"
              >
                Fertig
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 🆕 Password Display Dialog – zeigt Admin das Einmal-Passwort */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="max-w-md" data-testid="password-display-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <KeyRound className="h-5 w-5" />
                Mitarbeiter erfolgreich angelegt
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-950/20 border-2 border-green-300 dark:border-green-700 rounded-lg p-4">
                <p className="text-sm text-green-900 dark:text-green-100 font-semibold mb-1">
                  {createdEmployeeInfo.name}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">
                  {createdEmployeeInfo.email}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Automatisch generierter Einmal-PIN:
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 text-center">
                    <span className="text-3xl font-mono font-bold tracking-[0.3em] text-gray-900 dark:text-gray-100" data-testid="temp-password-display">
                      {createdEmployeeInfo.password}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0"
                    data-testid="copy-password-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(createdEmployeeInfo.password);
                      setPasswordCopied(true);
                      setTimeout(() => setPasswordCopied(false), 2000);
                    }}
                  >
                    {passwordCopied ? (
                      <><Check className="h-4 w-4 text-green-600 mr-1" /> Kopiert</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-1" /> Kopieren</>
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Wichtig:</strong> Kopieren Sie diesen PIN und geben Sie ihn an den Mitarbeiter weiter.
                  Beim ersten Login muss er ihn eingeben und sofort ein neues Passwort setzen.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={() => setShowPasswordDialog(false)}
                className="w-full"
                data-testid="password-dialog-close-btn"
              >
                Verstanden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Employee Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="h-5 w-5" />
                Mitarbeiter bearbeiten
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* E-Mail (jetzt editierbar) */}
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" /> E-Mail
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="edit-email-input"
                />
                {formData.email !== selectedEmployee?.email && (
                  <div className="flex items-start gap-2 mt-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      E-Mail wird geändert. Ein neuer temporärer PIN wird automatisch an die neue Adresse gesendet.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_first_name">Vorname *</Label>
                  <Input id="edit_first_name" name="first_name" value={formData.first_name} onChange={handleChange} />
                </div>
                <div>
                  <Label htmlFor="edit_last_name">Nachname *</Label>
                  <Input id="edit_last_name" name="last_name" value={formData.last_name} onChange={handleChange} />
                </div>
              </div>

              <div>
                <Label htmlFor="edit_role">Rolle</Label>
                <select id="edit_role" name="role" value={formData.role} onChange={handleChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="employee">Mitarbeiter</option>
                  <option value="stationsleitung">Stationsleitung</option>
                </select>
              </div>

              <div>
                <Label>Standorte (mehrere möglich)</Label>
                <div className="border rounded-lg p-3 mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {locations.map(location => (
                    <label key={location.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:bg-gray-800 rounded cursor-pointer">
                      <input type="checkbox" checked={formData.location_ids.includes(location.id)}
                        onChange={() => handleLocationToggle(location.id)} className="w-4 h-4" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{location.name}</p>
                        {location.address && <p className="text-xs text-gray-600 dark:text-gray-400">{location.address}</p>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Hardware-Felder */}
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Key className="h-4 w-4 text-gray-500" />
                  Hardware-Zuordnung
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Cpu className="h-3 w-3" /> Chip-Nummer</Label>
                    <Input name="chip_number" value={formData.chip_number || ''} onChange={handleChange}
                      placeholder="z.B. CH-0042" data-testid="chip-number-input" />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Key className="h-3 w-3" /> Schlüssel-Nummer</Label>
                    <Input name="key_number" value={formData.key_number || ''} onChange={handleChange}
                      placeholder="z.B. SL-007" data-testid="key-number-input" />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Erhalten am</Label>
                    <Input type="date" name="received_hardware_at" value={formData.received_hardware_at || ''}
                      onChange={handleChange} data-testid="received-hardware-input" />
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Zurückgegeben am</Label>
                    <Input type="date" name="returned_hardware_at" value={formData.returned_hardware_at || ''}
                      onChange={handleChange} data-testid="returned-hardware-input" />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 flex-col sm:flex-row pt-2 border-t mt-2">
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="w-full sm:w-auto border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400"
                data-testid="reset-password-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                {resettingPassword ? 'Wird gesendet...' : 'Passwort zurücksetzen'}
              </Button>
              <div className="flex gap-2 sm:ml-auto">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="w-full sm:w-auto">Abbrechen</Button>
                <Button onClick={handleUpdateEmployee} className="w-full sm:w-auto" data-testid="save-employee-btn">Speichern</Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hardware Export Dialog */}
      <Dialog open={showHardwareDialog} onOpenChange={setShowHardwareDialog}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5 text-blue-600" />
              Hardware-Liste exportieren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-3 block">Filter</Label>
              <div className="space-y-2">
                {[
                  { value: 'all', label: 'Alle Mitarbeiter' },
                  { value: 'active', label: 'Nur Aktive' },
                  { value: 'inactive', label: 'Nur Ausgeschiedene' },
                  { value: 'selection', label: 'Manuelle Auswahl' }
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input type="radio" name="hardwareFilter" value={opt.value} checked={hardwareFilter === opt.value}
                      onChange={() => { setHardwareFilter(opt.value); if (opt.value !== 'selection') setSelectedForExport([]); }}
                      className="rounded-full" />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {hardwareFilter === 'selection' && (
              <div>
                <Label className="text-xs mb-2 block">Mitarbeiter auswählen:</Label>
                <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center gap-2 p-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                      <input type="checkbox" checked={selectedForExport.includes(emp.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedForExport(prev => [...prev, emp.id]);
                          else setSelectedForExport(prev => prev.filter(id => id !== emp.id));
                        }} />
                      <span className="text-sm">{emp.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHardwareDialog(false)}>Abbrechen</Button>
            <Button onClick={handleHardwareExport} disabled={exportingPdf || (hardwareFilter === 'selection' && selectedForExport.length === 0)}
              data-testid="confirm-hardware-export-btn">
              <FileDown className="h-4 w-4 mr-1.5" />
              {exportingPdf ? 'Wird erstellt...' : 'PDF exportieren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 EMPLOYEE LIMIT DIALOG – billing_v2: Add-on buchen */}
      <Dialog open={showEmployeeLimitDialog} onOpenChange={setShowEmployeeLimitDialog}>
        <DialogContent className="max-w-md" data-testid="employee-limit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              Mitarbeiterlimit erreicht
            </DialogTitle>
          </DialogHeader>

          {employeeLimitDetail && (
            <div className="space-y-4">
              {/* Aktueller Stand */}
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4">
                <p className="font-semibold text-orange-900 dark:text-orange-100 mb-1">
                  {employeeLimitDetail.current}/{employeeLimitDetail.limit} Mitarbeiterplätze belegt
                </p>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Für weitere Mitarbeiter ist ein Add-on erforderlich.
                </p>
              </div>

              {/* Add-on Optionen */}
              <div className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  Verfügbare Mitarbeiter-Add-ons
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: '+5 MA', price: '5,99 €', slug: 'employees_plus_5' },
                    { label: '+10 MA', price: '9,99 €', slug: 'employees_plus_10' },
                    { label: '+20 MA', price: '17,99 €', slug: 'employees_plus_20' },
                  ].map(opt => (
                    <div key={opt.slug} className="text-center p-2 rounded-lg border bg-muted/30">
                      <p className="font-bold text-sm">{opt.label}</p>
                      <p className="text-xs text-muted-foreground">{opt.price}/Monat</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Netto-Preise, zzgl. 19% MwSt.</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEmployeeLimitDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => { setShowEmployeeLimitDialog(false); navigate('/admin/subscription'); }}
              className="bg-primary text-primary-foreground gap-1.5"
              data-testid="goto-addon-btn"
            >
              <ArrowUpRight className="h-4 w-4" />
              Add-on buchen → Abo-Seite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default EmployeeManagementEnhanced;
